import {
  ReportType,
  type ApiHealthDto,
  type ApiLivenessDto,
  type CrossValidationReportDto,
  type CrossValidationReportSummaryDto,
  type EstimatorReportComparisonDto,
  type EstimatorReportDto,
  type EstimatorReportSummaryDto,
  type InvitationDto,
  type ProjectDto,
  type ReportGroupDto,
  type UserAPIKeyDto,
  type UserDto,
  type WorkspaceDto,
} from "@/dto";
// here we need to import the router instance instead of using the composable
// because inject is not available
import { RouteNames, router } from "@/router";
import { apiDelete, apiGet, apiPost, checkResponseStatus } from "@/services/apiClient";
import { getErrorMessage, sha } from "@/services/utils";
import { useAuthStore } from "@/stores/auth";
import { useToastsStore } from "@/stores/toasts";

interface PaginatedResponse<T> {
  items: T[];
  next_cursor: number | null;
}

export function useApi() {
  const authStore = useAuthStore();

  function _reportError(message: string) {
    console.error(message);
    try {
      const toastsStore = useToastsStore();
      toastsStore.addToast(message, "error", { duration: 10 });
    } catch {}
  }

  function _redirectIfNotAuthenticated() {
    if (!authStore.isAuthenticated) {
      router.push({ name: RouteNames.Logout });
    }
  }

  async function isUserAuthenticated() {
    return await authStore.ensureUserIsAuthenticated();
  }

  /**
   * Check a device token against the IdP
   * @param state the state given by the IdP
   * @param user_code the otp
   * @returns true if success false otherwise
   */
  async function deviceCallback(state: string, user_code: string) {
    const result = await apiPost<boolean>(
      `/identity/oauth/device/callback`,
      { state, user_code },
      {
        defaultValue: false,
        onError: (error) => _reportError(getErrorMessage(error)),
      }
    );
    return result ?? false;
  }

  /**
   * Fetch a workspace the user has access to.
   * @param workspaceId
   * @returns a workspace dto
   */
  async function fetchWorkspace(workspaceId: number) {
    _redirectIfNotAuthenticated();
    const workspace = await apiGet<WorkspaceDto>(`/identity/workspaces/${workspaceId}`, {
      onError: (error) => _reportError(getErrorMessage(error)),
    });
    return workspace ?? null;
  }

  /**
   * Get the list of workspaces the user has access to.
   * @returns a list of workspaces
   */
  async function listWorkspaces() {
    _redirectIfNotAuthenticated();
    let nextCursor: number | null = 0;
    const workspaces = [];
    while (nextCursor !== null) {
      const data: PaginatedResponse<WorkspaceDto> | undefined = await apiGet<
        PaginatedResponse<WorkspaceDto>
      >(`/identity/workspaces/?cursor=${nextCursor}`, {
        onError: (error) => _reportError(getErrorMessage(error)),
      });
      if (data) {
        workspaces.push(...data.items);
        nextCursor = data.next_cursor;
      }
    }
    return workspaces;
  }

  /**
   * Check if a workspace name's generated slug is available and get a suggestion.
   */
  async function checkWorkspaceNameAvailability(name: string) {
    _redirectIfNotAuthenticated();
    const data = await apiGet<{ available: boolean; suggested_slug: string | null }>(
      `/identity/workspaces/name-availability?name=${name}`,
      {
        onError: (error) => _reportError(getErrorMessage(error)),
      }
    );
    return data ?? { available: false, suggested_slug: null };
  }

  /**
   * Create a workspace
   * @param name the workspace name
   * @param ownerId the owner id
   * @returns the newly created workspace or null if something went wrong.
   */
  async function createWorkspace(name: string, ownerId: string) {
    _redirectIfNotAuthenticated();
    const workspace = await apiPost<WorkspaceDto>(
      `/identity/workspaces/`,
      { name, owner_id: ownerId },
      {
        onError: (error) => _reportError(getErrorMessage(error)),
      }
    );
    return workspace?.id ?? null;
  }

  /**
   * Invite workspace members
   * @param workspaceId the workspace id
   * @param emails the emails of the members to invite
   * @returns true if the invitation has been sent false otherwise
   */
  async function inviteWorkspaceMembers(workspaceId: number, emails: string[]) {
    _redirectIfNotAuthenticated();

    const permissions = [
      "read:tenant",
      "create:tenant",
      "update:tenant",
      "delete:tenant",
      "read:project",
      "create:project",
      "update:project",
      "delete:project",
      "read:user",
      "create:user",
      "update:user",
      "delete:user",
      "read:invitation",
      "create:invitation",
      "delete:invitation",
    ];

    const invitations = emails.map((email) =>
      apiPost<InvitationDto>(`/identity/workspaces/${workspaceId}/invitations`, {
        workspace_id: workspaceId,
        role: "member",
        email,
        permissions,
      })
    );

    const results = await Promise.all(invitations);
    return results.every((result) => result !== null);
  }

  /**
   * Get the list of project the user has access to.
   * @param workspaceName workspace name in which we want to list project
   * @returns a list of project or null if not found
   */
  async function listProjects(workspaceName: string) {
    _redirectIfNotAuthenticated();
    const projects = await apiGet<ProjectDto[]>(`/projects/${workspaceName}`, {
      defaultValue: [],
      onError: (error) => _reportError(getErrorMessage(error)),
    });
    return projects;
  }

  /**
   * Delete project the user has access to.
   * @param workspaceName workspace name to which the project belongs
   * @param projectName the project's name
   * @returns a list of project
   * @returns true if delete has been done false otherwise.
   */
  async function deleteProject(workspaceName: string, projectName: string) {
    _redirectIfNotAuthenticated();
    const result = await apiDelete<boolean>(`/projects/${workspaceName}/${projectName}`, {
      defaultValue: false,
      onError: (error) => _reportError(getErrorMessage(error)),
    });
    return result ?? false;
  }

  /**
   * Get the experiment groups for a given project
   * @param workspaceName workspace name to which the project belongs
   * @param projectName the project's name
   * @param reportType the report type
   * @returns a list of experiments groups or null if not found
   */
  async function listReportGroups(
    workspaceName: string,
    projectName: string,
    reportType: ReportType
  ) {
    _redirectIfNotAuthenticated();
    const slug =
      reportType == ReportType.EstimatorReport ? "estimator-reports" : "cross-validation-reports";
    const groups = await apiGet<ReportGroupDto[]>(
      `/projects/${workspaceName}/${projectName}/${slug}/groups`,
      {
        onError: (error) => _reportError(getErrorMessage(error)),
      }
    );
    return groups;
  }

  /**
   * Filter estimator reports
   * @param workspaceName workspace name to which the project belongs
   * @param projectName the project's name
   * @param datasetFingerprint the dataset  to filter from
   * @param searchParams the search params to send
   * @returns a list of experiments groups
   */
  async function filterEstimatorReports(
    workspaceName: string,
    projectName: string,
    datasetFingerprint: string,
    searchParams: URLSearchParams
  ): Promise<EstimatorReportSummaryDto[]> {
    _redirectIfNotAuthenticated();
    searchParams.append("dataset_fingerprint", datasetFingerprint);
    const data = await apiGet<EstimatorReportSummaryDto[]>(
      `/projects/${workspaceName}/${projectName}/estimator-reports/?${searchParams}`,
      {
        defaultValue: [],
        onError: (error) => _reportError(getErrorMessage(error)),
      }
    );
    return data;
  }

  /**
   * Fetch estimator report
   * @param workspaceName workspace name to which the project belongs
   * @param projectName the project's name
   * @param reportId the report id
   */
  async function fetchEstimatorReport(
    workspaceName: string,
    projectName: string,
    reportId: number
  ) {
    _redirectIfNotAuthenticated();
    const report = await apiGet<EstimatorReportDto>(
      `/projects/${workspaceName}/${projectName}/estimator-reports/${reportId}`,
      {
        onError: (error) => _reportError(getErrorMessage(error)),
      }
    );
    return report ?? null;
  }

  /**
   * Filter cross validation reports
   * @param workspaceName workspace name to which the project belongs
   * @param projectName the project's name
   * @param datasetFingerprint the dataset  to filter from
   * @param searchParams the search params to send
   * @returns a list of experiments groups
   */
  async function filterCrossValidationReports(
    workspaceName: string,
    projectName: string,
    datasetFingerprint: string,
    searchParams: URLSearchParams
  ): Promise<CrossValidationReportSummaryDto[]> {
    _redirectIfNotAuthenticated();
    searchParams.append("dataset_fingerprint", datasetFingerprint);
    const data = await apiGet<CrossValidationReportSummaryDto[]>(
      `/projects/${workspaceName}/${projectName}/cross-validation-reports/?${searchParams}`,
      {
        defaultValue: [],
        onError: (error) => _reportError(getErrorMessage(error)),
      }
    );
    return data;
  }

  /**
   * Fetch cross validation report
   * @param workspaceName workspace name to which the project belongs
   * @param projectName the project's name
   * @param reportId the report id
   */
  async function fetchCrossValidationReport(
    workspaceName: string,
    projectName: string,
    reportId: number
  ) {
    _redirectIfNotAuthenticated();
    const report = await apiGet<CrossValidationReportDto>(
      `/projects/${workspaceName}/${projectName}/cross-validation-reports/${reportId}`,
      {
        onError: (error) => _reportError(getErrorMessage(error)),
      }
    );
    return report ?? null;
  }

  /**
   * Fetch a user by it's id
   * @param userId the if of the user to get
   */
  async function fetchUser(userId: string) {
    _redirectIfNotAuthenticated();
    const user = await apiGet<UserDto>(`/identity/users/${userId}`, {
      onError: (error) => _reportError(getErrorMessage(error)),
    });
    return user ?? null;
  }

  /**
   * Fetch the list of comparisons of a given project.
   * @param workspaceName workspace name to which the project belongs
   * @param projectName the project's name
   * @returns a list of comparisons.
   */
  async function listComparisons(workspaceName: string, projectName: string) {
    _redirectIfNotAuthenticated();
    const data = await apiGet<EstimatorReportComparisonDto[]>(
      `/projects/${workspaceName}/${projectName}/comparison-reports`,
      {
        defaultValue: [],
        onError: (error) => _reportError(getErrorMessage(error)),
      }
    );
    return data ?? [];
  }

  /**
   * Fetch a comparison from a given project.
   * @param workspaceName workspace name to which the project belongs
   * @param projectName the project's name
   * @param comparisonId the comparison id
   * @returns a comparison or null if something went wrong.
   */
  async function fetchComparison(workspaceName: string, projectName: string, comparisonId: number) {
    _redirectIfNotAuthenticated();
    const comparison = await apiGet<EstimatorReportComparisonDto>(
      `/projects/${workspaceName}/${projectName}/comparison-reports/${comparisonId}`,
      {
        onError: (error) => _reportError(getErrorMessage(error)),
      }
    );
    return comparison ?? null;
  }

  /**
   * Create a comparison in a given project.
   * @param workspaceName workspace name to which the project belongs
   * @param projectName the project's name
   * @param name the comparison's name
   * @param comparedReportsIds the complete list of compared reports ids
   * @param bestTradeOffsIds the list of chosen reports ids
   * @param bestTradeOffsExplanations a dict containing explanation for each choice
   * @returns the newly created comparison or null if something went wrong.
   */
  async function createComparisons(
    workspaceName: string,
    projectName: string,
    name: string,
    comparedReportsIds: number[],
    bestTradeOffsIds: number[],
    bestTradeOffsExplanations: Record<number, string>
  ) {
    _redirectIfNotAuthenticated();
    const comparison = await apiPost<EstimatorReportComparisonDto>(
      `/projects/${workspaceName}/${projectName}/comparison-reports`,
      {
        name,
        compared_reports: comparedReportsIds,
        best_trade_offs: bestTradeOffsIds,
        explanations: bestTradeOffsExplanations,
      },
      {
        onError: (error) => _reportError(getErrorMessage(error)),
      }
    );
    return comparison ?? null;
  }

  /**
   * Delete a comparison from a given project.
   * @param workspaceName workspace name to which the project belongs
   * @param projectName the project's name
   * @param comparisonId the comparison id
   * @returns true if deletion has been done false otherwise.
   */
  async function deleteComparison(
    workspaceName: string,
    projectName: string,
    comparisonId: number
  ) {
    _redirectIfNotAuthenticated();
    const result = await apiDelete<boolean>(
      `/projects/${workspaceName}/${projectName}/comparison-reports/${comparisonId}`,
      {
        defaultValue: false,
        onError: (error) => _reportError(getErrorMessage(error)),
      }
    );
    return result ?? false;
  }

  /**
   * Fetch API health status.
   */
  async function fetchLiveness() {
    const liveness = await apiGet<ApiLivenessDto>(`/liveness`, {
      onError: (error) => _reportError(getErrorMessage(error)),
    });
    return liveness ?? null;
  }

  /**
   * Fetch API health status.
   */
  async function fetchHealth() {
    const health = await apiGet<ApiHealthDto>(`/health`, {
      onError: (error) => _reportError(getErrorMessage(error)),
    });
    return health ?? null;
  }

  async function checksumArtifact(content: string | Blob) {
    const hash = await sha(content.toString());
    return `sha1-${hash}`;
  }

  async function uploadArtifact(
    workspaceName: string,
    projectName: string,
    content_type: string,
    content: string | Blob
  ) {
    try {
      _redirectIfNotAuthenticated();
      const checksum = await checksumArtifact(content);

      const data = await apiPost<Array<{ upload_url?: string }>>(
        `/projects/${workspaceName}/${projectName}/artifacts/`,
        [{ checksum, content_type }],
        {
          onError: (error) => _reportError(getErrorMessage(error)),
        }
      );

      if (data && data.length > 0 && data[0]) {
        // artifact does not exist - upload to S3
        const uploadUrl = data[0].upload_url;
        if (uploadUrl) {
          // S3 upload - keep raw fetch as it's external API
          const s3Request = await fetch(uploadUrl, {
            method: "PUT",
            headers: { "Content-Type": content_type },
            body: content,
          });
          checkResponseStatus(s3Request, 200);

          // Complete the upload
          await apiPost(
            `/projects/${workspaceName}/${projectName}/artifacts/complete`,
            [{ checksum, etags: {} }],
            {
              expectedStatus: 200,
              onError: (error) => _reportError(getErrorMessage(error)),
            }
          );
        }
      }

      return checksum;
    } catch (error) {
      _reportError(getErrorMessage(error));
    }
  }

  /**
   * Fetch user's API keys
   * @param userId the id of the user to fetch the API keys for
   * @returns a list of API keys
   */
  async function fetchUserApiKeys(userId: string) {
    _redirectIfNotAuthenticated();
    const apiKeys = await apiGet<UserAPIKeyDto[]>(`/identity/users/${userId}/api-keys`, {
      onError: (error) => _reportError(getErrorMessage(error)),
    });
    return apiKeys ?? [];
  }

  /**
   * Create a user API key
   * @param userId the id of the user to create the API key for
   * @param name the name of the API key
   * @returns the newly created API key
   */
  async function createUserApiKey(userId: string, name: string) {
    _redirectIfNotAuthenticated();
    const result = await apiPost<{
      api_key_id: number;
      api_key: string;
    }>(
      `/identity/users/${userId}/api-keys`,
      {
        name,
        permissions: [
          "read:workspace",
          "create:workspace",
          "update:workspace",
          "delete:workspace",
          "read:project",
          "create:project",
          "update:project",
          "delete:project",
          "read:user",
          "create:user",
          "update:user",
          "delete:user",
          "read:invitation",
          "create:invitation",
          "delete:invitation",
        ],
      },
      {
        onError: (error) => _reportError(getErrorMessage(error)),
      }
    );
    return result?.api_key ?? null;
  }

  /**
   * Delete a user API key
   * @param userId the id of the user to delete the API key for
   * @param apiKeyId the id of the API key to delete
   */
  async function deleteUserApiKey(userId: string, apiKeyId: number) {
    _redirectIfNotAuthenticated();
    await apiDelete<void>(`/identity/users/${userId}/api-keys/${apiKeyId}`, {
      onError: (error) => _reportError(getErrorMessage(error)),
    });
  }

  return {
    checksumArtifact,
    checkWorkspaceNameAvailability,
    createUserApiKey,
    createComparisons,
    createWorkspace,
    deleteComparison,
    deleteUserApiKey,
    deleteProject,
    deviceCallback,
    fetchComparison,
    fetchCrossValidationReport,
    fetchEstimatorReport,
    fetchHealth,
    fetchLiveness,
    fetchUser,
    fetchUserApiKeys,
    fetchWorkspace,
    filterCrossValidationReports,
    filterEstimatorReports,
    inviteWorkspaceMembers,
    isUserAuthenticated,
    listComparisons,
    listProjects,
    listReportGroups,
    listWorkspaces,
    uploadArtifact,
  };
}
